import React from "react";
import "./EegDockPanelLabeling.css";

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export type EegDockPanelLabelingProps = {
  className?: string;
  "data-testid"?: string;
  ariaLabel?: string;
  /** Compact status chip shown on the Labeling disclosure summary. */
  badge?: string;
  /** One-line in-app summary (inside Labeling disclosure). */
  summary: string;
  /** Full frozen intended-use text (shown in Labeling disclosure). */
  intendedUse: string;
  /** Full frozen excluded-use text (shown in Labeling disclosure). */
  excludedUse?: string;
  /** Extra labeling prose (e.g. alert policy) inside the disclosure. */
  extraDetails?: React.ReactNode;
  /** Compact session/runtime notes that stay visible outside the disclosure. */
  sessionNotes?: React.ReactNode;
};

/**
 * Compact IFU chrome: collapsed row shows badge + "Labeling"; full summary and
 * frozen labeling language live under the disclosure (and the product User
 * Manual / QMS IFU freeze).
 */
export function EegDockPanelLabeling({
  className,
  "data-testid": dataTestId,
  ariaLabel = "Product labeling",
  badge,
  summary,
  intendedUse,
  excludedUse,
  extraDetails,
  sessionNotes,
}: EegDockPanelLabelingProps) {
  return (
    <div
      className={cx(
        "eeg-dock-panel-chrome__note",
        "eeg-dock-panel-labeling",
        className,
      )}
      role="note"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <details
        className="eeg-dock-panel-labeling__details"
        data-testid={
          dataTestId
            ? `${dataTestId}-details`
            : "eeg-dock-panel-labeling-details"
        }
      >
        <summary className="eeg-dock-panel-labeling__toggle">
          {badge ? (
            <span
              className="eeg-dock-panel-labeling__badge"
              data-testid={
                dataTestId
                  ? `${dataTestId}-badge`
                  : "eeg-dock-panel-labeling-badge"
              }
            >
              {badge}
            </span>
          ) : null}
          <span className="eeg-dock-panel-labeling__toggle-text">Labeling</span>
        </summary>
        <div className="eeg-dock-panel-labeling__full">
          <p
            className="eeg-dock-panel-labeling__summary"
            data-testid={
              dataTestId
                ? `${dataTestId}-summary`
                : "eeg-dock-panel-labeling-summary"
            }
          >
            {summary}
          </p>
          <p>
            <strong>Intended use:</strong> {intendedUse}
          </p>
          {excludedUse ? <p>{excludedUse}</p> : null}
          {extraDetails}
          <p className="eeg-dock-panel-labeling__manual-hint">
            Full IFU language is maintained in the product User Manual and
            regulatory labeling freeze.
          </p>
        </div>
      </details>

      {sessionNotes ? (
        <div className="eeg-dock-panel-labeling__session-notes">
          {sessionNotes}
        </div>
      ) : null}
    </div>
  );
}

export default EegDockPanelLabeling;
